import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { buildApiUrl, getApiBaseUrl } from '../../config/apiConfig';
import './ClassPlayerPage.css';
import { log, logError, warn } from '../../utils/logger';

/**
 * Vimeo: enlaces privados / no listados usan vimeo.com/VIDEO_ID/HASH.
 * El embed correcto es player.vimeo.com/video/VIDEO_ID?h=HASH (no /VIDEO_ID/HASH en la ruta).
 */
function parseVimeoUrl(url) {
  try {
    const u = new URL(url.trim());
    const host = u.hostname.replace(/^www\./, '');

    if (host === 'player.vimeo.com') {
      const m = u.pathname.match(/^\/video\/(\d+)(?:\/([^/?#]+))?\/?$/);
      if (!m) return null;
      const id = m[1];
      const hashFromPath = m[2];
      const h = u.searchParams.get('h') || hashFromPath || null;
      return { id, h };
    }

    if (host === 'vimeo.com') {
      const parts = u.pathname.split('/').filter(Boolean);
      if (parts.length >= 1 && /^\d+$/.test(parts[0])) {
        const id = parts[0];
        const second = parts[1];
        const h =
          u.searchParams.get('h') ||
          (second && !/^\d+$/.test(second) ? second : null);
        return { id, h };
      }
    }
  } catch {
    return null;
  }
  return null;
}

function buildVimeoEmbedUrl({ id, h }) {
  const qs = new URLSearchParams();
  qs.set('autoplay', '1');
  qs.set('responsive', '1');
  qs.set('badge', '0');
  qs.set('autopause', '0');
  if (h) qs.set('h', h);
  return `https://player.vimeo.com/video/${id}?${qs.toString()}`;
}

const ClassPlayerPage = () => {
  const { courseId, classId } = useParams();
  const { user, isAuthenticated, token } = useAuth();
  const { notify } = useNotifications();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [currentClass, setCurrentClass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [videoEmbedUrl, setVideoEmbedUrl] = useState(null);
  const [markingComplete, setMarkingComplete] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [progress, setProgress] = useState(null);
  const [videoType, setVideoType] = useState(null); // 'youtube', 'vimeo', or null
  const [videoId, setVideoId] = useState(null);
  
  const iframeRef = useRef(null);
  const youtubePlayerRef = useRef(null);
  const vimeoPlayerRef = useRef(null);
  const autoMarkedRef = useRef(false); // Para evitar marcar múltiples veces
  /** Lectura actual en listeners del player sin re-disparar el efecto (evita destroy → pantalla negra). */
  const isCompletedRef = useRef(false);
  const markingCompleteRef = useRef(false);
  const markCompleteNavRef = useRef(() => {});

  // Función auxiliar para obtener la siguiente clase
  const getNextClass = useCallback(() => {
    if (!course?.modulos) {
      log('⚠️ No hay módulos en el curso');
      return null;
    }

    let found = false;
    for (const modulo of course.modulos) {
      if (!modulo.clases) continue;
      const sortedClasses = [...modulo.clases].sort((a, b) => (a.orden || 0) - (b.orden || 0));
      
      for (let i = 0; i < sortedClasses.length; i++) {
        if (found) {
          log('✅ Siguiente clase encontrada en el mismo módulo:', sortedClasses[i].id);
          return { class: sortedClasses[i], module: modulo };
        }
        if (sortedClasses[i].id === parseInt(classId)) {
          found = true;
          log('📍 Clase actual encontrada:', sortedClasses[i].id);
        }
      }
    }

    // Si no hay siguiente en el mismo módulo, buscar en el siguiente módulo
    if (found) {
      const currentModuleIndex = course.modulos.findIndex(
        (m) => m.id === currentClass?.module?.id
      );
      log('🔍 Buscando en siguiente módulo. Módulo actual:', currentModuleIndex);
      if (currentModuleIndex < course.modulos.length - 1) {
        const nextModule = course.modulos[currentModuleIndex + 1];
        if (nextModule.clases && nextModule.clases.length > 0) {
          const sortedClasses = [...nextModule.clases].sort((a, b) => (a.orden || 0) - (b.orden || 0));
          log('✅ Siguiente clase encontrada en siguiente módulo:', sortedClasses[0].id);
          return { class: sortedClasses[0], module: nextModule };
        }
      }
    }

    log('⚠️ No se encontró siguiente clase');
    return null;
  }, [course, classId, currentClass]);

  // Definir handleMarkAsCompleteAndNavigate con useCallback
  const handleMarkAsCompleteAndNavigate = useCallback(async (isAuto = false) => {
    if (markingComplete || isCompleted || autoMarkedRef.current) {
      log('⚠️ Ya está marcando o completada:', { markingComplete, isCompleted, autoMarked: autoMarkedRef.current });
      return;
    }

    log('🔄 Iniciando marcado de clase como completada...', { classId, isAuto });
    // Establecer autoMarkedRef ANTES de setMarkingComplete para evitar múltiples llamadas
    autoMarkedRef.current = true;
    setMarkingComplete(true);
    try {
      // Obtener progreso actual
      const progressResponse = await fetch(buildApiUrl(`/api/user/courses/progress/${courseId}`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      let currentProgress = { completedClasses: [] };
      if (progressResponse.ok) {
        currentProgress = await progressResponse.json();
        log('📊 Progreso actual obtenido:', currentProgress);
      }

      const completedClasses = currentProgress.completedClasses || [];
      const classIdNum = parseInt(classId);

      if (!completedClasses.includes(classIdNum)) {
        completedClasses.push(classIdNum);
        log('➕ Agregando clase a completadas:', classIdNum);
      } else {
        log('ℹ️ La clase ya estaba en completadas');
      }

      // Calcular nuevo porcentaje
      const totalClasses = course?.modulos?.reduce(
        (sum, m) => sum + (m.clases?.length || 0),
        0
      ) || 1;
      const newProgressPercent = totalClasses > 0 
        ? Math.round((completedClasses.length / totalClasses) * 100) 
        : 0;
      
      log('📊 Cálculo de progreso:', {
        completedClasses: completedClasses.length,
        totalClasses,
        newProgressPercent: `${newProgressPercent}%`
      });

      // Usar la nueva API para marcar la clase como completada
      const markClassResponse = await fetch(buildApiUrl(`/api/user/classes/${classIdNum}/complete`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!markClassResponse.ok) {
        const errorText = await markClassResponse.text();
        logError('❌ Error marcando clase como completada:', markClassResponse.status, errorText);
        throw new Error('Error al marcar la clase como completada');
      }

      const markClassData = await markClassResponse.json();
      log('✅ Clase marcada como completada:', markClassData);

      // Obtener el progreso actualizado del servidor
      const updateResponse = await fetch(buildApiUrl(`/api/user/courses/progress/${courseId}`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (updateResponse.ok) {
        const finalProgressData = await updateResponse.json();
        log('✅ Progreso actualizado del servidor:', finalProgressData);
        
        // Asegurar que completedClasses sea un array
        const finalCompletedClasses = Array.isArray(finalProgressData.completedClasses) 
          ? [...finalProgressData.completedClasses] 
          : (Array.isArray(markClassData.completedClasses) ? [...markClassData.completedClasses] : []);
        
        const updatedProgress = {
          ...finalProgressData,
          completedClasses: finalCompletedClasses,
          progressPercent: finalProgressData.progressPercent || markClassData.progressPercent || newProgressPercent,
          completed: finalProgressData.completed || markClassData.completed || false,
          totalClasses: finalProgressData.totalClasses || markClassData.totalClasses,
          completedClassesCount: finalCompletedClasses.length,
        };
        
        log('✅ Progreso actualizado:', {
          completedClasses: updatedProgress.completedClasses,
          progressPercent: `${updatedProgress.progressPercent}%`,
          classId: classIdNum,
          totalCompletadas: updatedProgress.completedClasses.length,
          totalClasses: updatedProgress.totalClasses,
        });
        
        setIsCompleted(true);
        autoMarkedRef.current = true;
        
        // Actualizar el estado de progreso local - crear nueva referencia para forzar re-render
        setProgress(updatedProgress);
        
        log('🔄 Estado de progreso actualizado en React:', {
          ...updatedProgress,
          completedClassesCount: updatedProgress.completedClasses.length
        });
        
        if (isAuto) {
          notify({
            message: '¡Clase completada! Pasando a la siguiente en 5 segundos...',
            type: 'success',
          });

          // Esperar 5 segundos y luego navegar a la siguiente clase
          setTimeout(() => {
            const next = getNextClass();
            log('🔍 Buscando siguiente clase después de 5 segundos:', { next, currentClassId: classId });
            if (next && next.class) {
              log('➡️ Navegando a la siguiente clase:', next.class.id);
              navigate(`/my-courses/${courseId}/class/${next.class.id}`);
            } else {
              log('✅ No hay más clases, curso completado');
              notify({
                message: '¡Has completado todas las clases de este curso!',
                type: 'success',
              });
              // Redirigir a la página de contenido del curso después de 2 segundos
              setTimeout(() => {
                navigate(`/my-courses/${courseId}/content`);
              }, 2000);
            }
          }, 5000);
        } else {
          notify({
            message: '¡Clase marcada como completada!',
            type: 'success',
          });
        }
      } else {
        const errorText = await updateResponse.text();
        logError('❌ Error en la respuesta del servidor:', updateResponse.status, errorText);
        throw new Error('Error al actualizar el progreso');
      }
    } catch (err) {
      logError('[ClassPlayerPage] Error marking as complete:', err);
      notify({
        message: 'Error al marcar la clase como completada.',
        type: 'error',
      });
    } finally {
      setMarkingComplete(false);
    }
  }, [courseId, classId, token, course, isCompleted, markingComplete, notify, navigate, getNextClass, currentClass]);

  isCompletedRef.current = isCompleted;
  markingCompleteRef.current = markingComplete;
  markCompleteNavRef.current = handleMarkAsCompleteAndNavigate;

  useEffect(() => {
    if (!isAuthenticated) {
      notify({
        message: 'Debes iniciar sesión para ver las clases.',
        type: 'info',
      });
      navigate('/login', { state: { from: `/my-courses/${courseId}/content` } });
      return;
    }

    fetchClassData();
    autoMarkedRef.current = false; // Reset cuando cambia la clase
    setIsCompleted(false); // Reset estado de completado cuando cambia la clase
  }, [courseId, classId, isAuthenticated, token, navigate, notify]);

  // Recargar progreso cuando cambia la clase para asegurar que la barra lateral se actualice
  useEffect(() => {
    if (!isAuthenticated || !token || !courseId) return;

    const refreshProgress = async () => {
      try {
        const progressResponse = await fetch(buildApiUrl(`/api/user/courses/progress/${courseId}`), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (progressResponse.ok) {
          const progressData = await progressResponse.json();
          setProgress(progressData);
          const completed = progressData.completedClasses?.includes(parseInt(classId)) || false;
          setIsCompleted(completed);
          log('🔄 Progreso refrescado al cambiar de clase:', {
            classId,
            completed,
            completedClasses: progressData.completedClasses
          });
        }
      } catch (err) {
        logError('Error refrescando progreso:', err);
      }
    };

    refreshProgress();
  }, [classId, courseId, token, isAuthenticated]);

  // Cargar YouTube IFrame API
  useEffect(() => {
    if (videoType === 'youtube' && videoId && !window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

      window.onYouTubeIframeAPIReady = () => {
        log('✅ YouTube IFrame API cargada');
      };
    }
  }, [videoType, videoId]);

  // Cargar Vimeo Player API
  useEffect(() => {
    if (videoType === 'vimeo' && videoId && !window.Vimeo) {
      const tag = document.createElement('script');
      tag.src = 'https://player.vimeo.com/api/player.js';
      tag.onload = () => {
        log('✅ Vimeo Player API cargada');
      };
      document.body.appendChild(tag);
    }
  }, [videoType, videoId]);

  // Inicializar players y configurar listeners (deps solo video/clase: isCompleted y markComplete
  // van por refs para no ejecutar destroy() al completar → iframe en negro).
  useEffect(() => {
    if (!videoType || !videoId || !iframeRef.current) {
      return;
    }

    let cancelled = false;
    let waitInterval = null;
    let initDelayTimer = null;

    const initializeVideoPlayer = async () => {
      if (cancelled || !iframeRef.current) return;

      if (videoType === 'youtube' && window.YT && window.YT.Player) {
        try {
          if (youtubePlayerRef.current) {
            youtubePlayerRef.current.destroy();
          }

          const iframeId = `youtube-player-${classId}`;
          if (iframeRef.current) {
            if (!iframeRef.current.id) {
              iframeRef.current.id = iframeId;
            }

            youtubePlayerRef.current = new window.YT.Player(iframeRef.current, {
              events: {
                onStateChange: (event) => {
                  if (event.data === window.YT.PlayerState.ENDED) {
                    log('📺 Video de YouTube terminado, marcando como completada automáticamente');
                    if (
                      !isCompletedRef.current &&
                      !autoMarkedRef.current &&
                      !markingCompleteRef.current
                    ) {
                      log('✅ Condiciones cumplidas, procediendo a marcar...');
                      markCompleteNavRef.current(true);
                    } else {
                      log('⚠️ No se puede marcar:', {
                        isCompleted: isCompletedRef.current,
                        autoMarked: autoMarkedRef.current,
                        markingComplete: markingCompleteRef.current,
                      });
                    }
                  }
                },
              },
            });
            log('✅ YouTube Player inicializado');
          }
        } catch (err) {
          logError('❌ Error inicializando YouTube Player:', err);
        }
      } else if (videoType === 'vimeo' && window.Vimeo) {
        try {
          if (vimeoPlayerRef.current) {
            vimeoPlayerRef.current.destroy();
          }

          vimeoPlayerRef.current = new window.Vimeo.Player(iframeRef.current);

          vimeoPlayerRef.current.on('ended', () => {
            log('📺 Video de Vimeo terminado, marcando como completada automáticamente');
            if (
              !isCompletedRef.current &&
              !autoMarkedRef.current &&
              !markingCompleteRef.current
            ) {
              log('✅ Condiciones cumplidas, procediendo a marcar...');
              markCompleteNavRef.current(true);
            } else {
              log('⚠️ No se puede marcar:', {
                isCompleted: isCompletedRef.current,
                autoMarked: autoMarkedRef.current,
                markingComplete: markingCompleteRef.current,
              });
            }
          });

          log('✅ Vimeo Player inicializado');
        } catch (err) {
          logError('❌ Error inicializando Vimeo Player:', err);
        }
      }
    };

    const scheduleInit = () => {
      initDelayTimer = window.setTimeout(() => {
        initDelayTimer = null;
        if (!cancelled) initializeVideoPlayer();
      }, 500);
    };

    if (videoType === 'youtube') {
      if (window.YT && window.YT.Player) {
        scheduleInit();
      } else {
        waitInterval = window.setInterval(() => {
          if (window.YT && window.YT.Player) {
            if (waitInterval) clearInterval(waitInterval);
            waitInterval = null;
            scheduleInit();
          }
        }, 100);
      }
    } else if (videoType === 'vimeo') {
      if (window.Vimeo) {
        scheduleInit();
      } else {
        waitInterval = window.setInterval(() => {
          if (window.Vimeo) {
            if (waitInterval) clearInterval(waitInterval);
            waitInterval = null;
            scheduleInit();
          }
        }, 100);
      }
    }

    return () => {
      cancelled = true;
      if (waitInterval) clearInterval(waitInterval);
      if (initDelayTimer) clearTimeout(initDelayTimer);
      if (youtubePlayerRef.current) {
        try {
          youtubePlayerRef.current.destroy();
        } catch (e) {
          warn('Error destruyendo YouTube Player:', e);
        }
        youtubePlayerRef.current = null;
      }
      if (vimeoPlayerRef.current) {
        try {
          vimeoPlayerRef.current.destroy();
        } catch (e) {
          warn('Error destruyendo Vimeo Player:', e);
        }
        vimeoPlayerRef.current = null;
      }
    };
  }, [videoType, videoId, classId]);

  const fetchClassData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Verificar que el usuario tiene acceso al curso
      const checkResponse = await fetch(buildApiUrl(`/api/user/check-course/${courseId}`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!checkResponse.ok || !(await checkResponse.json()).hasCourse) {
        notify({
          message: 'No tienes acceso a este curso.',
          type: 'warning',
        });
        navigate('/my-courses');
        return;
      }

      // Obtener el curso completo con módulos y clases
      const courseResponse = await fetch(buildApiUrl(`/api/courses/${courseId}`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!courseResponse.ok) {
        throw new Error('Error al cargar el curso');
      }

      const courseData = await courseResponse.json();
      const courseInfo = courseData.course || courseData;
      setCourse(courseInfo);

      // Buscar la clase específica
      let foundClass = null;
      if (courseInfo.modulos) {
        for (const modulo of courseInfo.modulos) {
          if (modulo.clases) {
            foundClass = modulo.clases.find((c) => c.id === parseInt(classId));
            if (foundClass) {
              foundClass.module = modulo;
              break;
            }
          }
        }
      }

      if (!foundClass) {
        throw new Error('Clase no encontrada');
      }

      setCurrentClass(foundClass);

      // Obtener progreso para verificar si está completada
      const progressResponse = await fetch(buildApiUrl(`/api/user/courses/progress/${courseId}`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (progressResponse.ok) {
        const progressData = await progressResponse.json();
        setProgress(progressData);
        const completed = progressData.completedClasses?.includes(parseInt(classId)) || false;
        setIsCompleted(completed);
      }

      // Procesar URL del video
      const videoUrl = foundClass.linkVideo || foundClass.videoUrl;
      if (videoUrl) {
        const { embedUrl, type, id } = convertToEmbedUrl(videoUrl);
        setVideoEmbedUrl(embedUrl);
        setVideoType(type);
        setVideoId(id);
      }
    } catch (err) {
      logError('[ClassPlayerPage] Error fetching class data:', err);
      setError(err.message || 'Error al cargar la clase.');
      notify({
        message: err.message || 'Error al cargar la clase.',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const convertToEmbedUrl = (url) => {
    if (!url) return { embedUrl: null, type: null, id: null };

    // YouTube: youtube.com/watch?v=ID o youtu.be/ID
    if (url.includes('youtube.com/watch')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      if (videoId) {
        return {
          embedUrl: `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&autoplay=1&enablejsapi=1`,
          type: 'youtube',
          id: videoId,
        };
      }
    } else if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      if (videoId) {
        return {
          embedUrl: `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&autoplay=1&enablejsapi=1`,
          type: 'youtube',
          id: videoId,
        };
      }
    } else if (url.includes('youtube.com/embed/')) {
      // Ya está en formato embed, extraer el ID
      const videoId = url.split('embed/')[1]?.split('?')[0];
      const embedUrl = url.includes('enablejsapi=1') 
        ? url 
        : url.includes('?') 
          ? `${url}&enablejsapi=1` 
          : `${url}?enablejsapi=1`;
      return {
        embedUrl,
        type: 'youtube',
        id: videoId,
      };
    }
    // Vimeo: vimeo.com/ID, vimeo.com/ID/HASH (privado), player.vimeo.com/video/ID?h=HASH
    else if (url.includes('vimeo.com/')) {
      const parsed = parseVimeoUrl(url);
      if (parsed) {
        return {
          embedUrl: buildVimeoEmbedUrl(parsed),
          type: 'vimeo',
          id: parsed.id,
        };
      }
    }
    // Si no es una URL reconocida, devolver null
    return { embedUrl: null, type: null, id: null };
  };


  const getPreviousClass = () => {
    if (!course?.modulos) return null;

    let previousClass = null;
    let previousModule = null;

    for (const modulo of course.modulos) {
      if (!modulo.clases) continue;
      const sortedClasses = [...modulo.clases].sort((a, b) => (a.orden || 0) - (b.orden || 0));
      
      for (let i = 0; i < sortedClasses.length; i++) {
        if (sortedClasses[i].id === parseInt(classId)) {
          return previousClass ? { class: previousClass, module: previousModule } : null;
        }
        previousClass = sortedClasses[i];
        previousModule = modulo;
      }
    }

    return null;
  };



  if (loading) {
    return (
      <div className="class-player-page loading-state">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando clase...</span>
        </div>
        <p>Cargando clase...</p>
      </div>
    );
  }

  if (error || !currentClass) {
    return (
      <div className="class-player-page error-state">
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error || 'Clase no encontrada'}
        </div>
        <button className="btn btn-primary mt-3" onClick={() => navigate(`/my-courses/${courseId}/content`)}>
          <i className="fas fa-arrow-left me-2"></i>
          Volver al Contenido del Curso
        </button>
      </div>
    );
  }


  return (
    <div className="class-player-page">
      {/* Header */}
      <div className="class-player-header">
        <div className="class-player-header-content">
          <button
            className="btn btn-link back-button"
            onClick={() => navigate(`/my-courses/${courseId}/content`)}
          >
            <i className="fas fa-arrow-left me-2"></i>
            Volver al Contenido
          </button>
          <div className="class-breadcrumb">
            <span onClick={() => navigate('/my-courses')}>{course?.titulo || course?.nombre}</span>
            <i className="fas fa-chevron-right"></i>
            <span>{currentClass.module?.nombre || 'Módulo'}</span>
            <i className="fas fa-chevron-right"></i>
            <span className="active">{currentClass.nombre}</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="class-player-body">
        <div className="class-player-container">
          <div className="class-player-layout">
            {/* Left Column - 3/4 */}
            <div className="class-player-main">
              {/* Video Section */}
              <div className="class-player-video-section">
                <div className="video-container">
                  {videoEmbedUrl ? (
                    <iframe
                      key={`embed-${classId}-${videoId || ''}`}
                      ref={iframeRef}
                      src={videoEmbedUrl}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title={currentClass.nombre}
                    ></iframe>
                  ) : currentClass.linkVideo || currentClass.videoUrl ? (
                    <div className="video-placeholder">
                      <i className="fas fa-play-circle"></i>
                      <p>Video no disponible en formato embebido</p>
                      <a
                        href={currentClass.linkVideo || currentClass.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary mt-3"
                      >
                        <i className="fas fa-external-link-alt me-2"></i>
                        Ver Video Externo
                      </a>
                    </div>
                  ) : (
                    <div className="video-placeholder">
                      <i className="fas fa-video"></i>
                      <p>Esta clase no tiene video disponible</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Class Info Below Video */}
              <div className="class-info-section">
                <div className="class-info-header">
                  <h1 className="class-title">{currentClass.nombre}</h1>
                  {isCompleted && (
                    <span className="completed-badge">
                      <i className="fas fa-check-circle me-1"></i>
                      Completada
                    </span>
                  )}
                </div>

                {currentClass.descripcionCompleta && (
                  <div className="class-full-description">
                    <p>{currentClass.descripcionCompleta}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - 1/4 - Modules and Classes List */}
            <div className="class-player-sidebar">
              <div className="sidebar-header">
                <h3>
                  <i className="fas fa-list-ul me-2"></i>
                  Contenido del Curso
                </h3>
              </div>
              <div className="sidebar-content">
                {course?.modulos && course.modulos.length > 0 ? (
                  <div className="sidebar-modules">
                    {course.modulos.map((modulo, moduleIndex) => {
                      const moduleClasses = modulo.clases || [];
                      const sortedClasses = [...moduleClasses].sort((a, b) => (a.orden || 0) - (b.orden || 0));

                      return (
                        <div key={modulo.id || moduleIndex} className="sidebar-module">
                          <div className="sidebar-module-header">
                            <i className="fas fa-folder"></i>
                            <span className="sidebar-module-title">
                              {modulo.nombre || `Módulo ${moduleIndex + 1}`}
                            </span>
                          </div>
                          <div className="sidebar-classes">
                            {sortedClasses.map((clase, classIndex) => {
                              const isCurrentClass = clase.id === parseInt(classId);
                              // Verificar si la clase está completada
                              // Usar el estado progress actualizado y también verificar si es la clase actual y está completada
                              const completedClassesList = Array.isArray(progress?.completedClasses) 
                                ? progress.completedClasses 
                                : [];
                              const classIdNum = parseInt(clase.id);
                              const isInCompletedList = completedClassesList.includes(classIdNum);
                              const isClassCompleted = isInCompletedList || (isCurrentClass && isCompleted);

                              return (
                                <div
                                  key={clase.id || classIndex}
                                  className={`sidebar-class-item ${isCurrentClass ? 'active' : ''} ${isClassCompleted ? 'completed' : ''}`}
                                  onClick={() => navigate(`/my-courses/${courseId}/class/${clase.id}`)}
                                >
                                  <div className="sidebar-class-icon">
                                    {isClassCompleted ? (
                                      <i className="fas fa-check-circle"></i>
                                    ) : (
                                      <i className="fas fa-play-circle"></i>
                                    )}
                                  </div>
                                  <div className="sidebar-class-content">
                                    <span className="sidebar-class-title">{clase.nombre}</span>
                                    {clase.minutes > 0 && (
                                      <span className="sidebar-class-duration">{clase.minutes} min</span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="sidebar-empty">
                    <p>No hay contenido disponible</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassPlayerPage;

